/**
 * Error logging
 *
 * Can be used to log errors to console or and eternal
 * error logging system, like Sentry for example.
 *
 */

import config from '../config';
import { responseApiErrorInfo } from './errors';

// Create a placeholder for Sentry that will be loaded only when needed
let SentryPromise = null;

/**
 * Dynamically imports Sentry only when needed.
 *
 * Imports `./sentryClient` rather than `@sentry/react` directly: that module
 * re-exports only the handful of functions used below as static named
 * imports, which is what lets webpack tree-shake the rest of the SDK barrel
 * (Replay/rrweb, Feedback, Profiling, Tracing) out of this chunk. Adding a new
 * `Sentry.<x>` call below therefore requires adding `x` to sentryClient.js.
 *
 * @returns {Promise} Promise that resolves with the Sentry module
 */
const loadSentry = () => {
  if (!SentryPromise && config.sentryDsn) {
    // Dynamic import to create a separate chunk for Sentry
    SentryPromise = import(/* webpackChunkName: "sentry" */ './sentryClient');
  }
  return SentryPromise;
};

// Uncaught errors/rejections that happen before the (deferred) Sentry init are
// buffered here and captured right after init, so deferring Sentry out of the
// load window doesn't lose crashes from exactly that window.
const EARLY_ERROR_BUFFER_LIMIT = 20;
let earlyErrors = [];
let earlyListenersAttached = false;

const onEarlyError = (event) => {
  const error = event?.error || event?.reason;
  if (error && earlyErrors.length < EARLY_ERROR_BUFFER_LIMIT) {
    earlyErrors.push(error);
  }
};

const attachEarlyErrorListeners = () => {
  if (earlyListenersAttached || typeof window === 'undefined') return;
  earlyListenersAttached = true;
  window.addEventListener('error', onEarlyError);
  window.addEventListener('unhandledrejection', onEarlyError);
};

const detachEarlyErrorListeners = () => {
  if (!earlyListenersAttached) return;
  earlyListenersAttached = false;
  window.removeEventListener('error', onEarlyError);
  window.removeEventListener('unhandledrejection', onEarlyError);
};

// Runs `cb` once the browser is idle after the window `load` event — i.e.
// outside the LCP/INP-relevant load window that Core Web Vitals measure.
const runWhenIdleAfterLoad = (cb) => {
  const whenIdle = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(cb, { timeout: 10000 });
    } else {
      // Safari has no requestIdleCallback
      setTimeout(cb, 3000);
    }
  };
  if (document.readyState === 'complete') {
    whenIdle();
  } else {
    window.addEventListener('load', whenIdle, { once: true });
  }
};

// User identity requested before Sentry finished initializing.
// undefined = never set, null = explicitly cleared, object = user.
let pendingUser;

let sentryInitPromise = null;

/**
 * Loads AND initializes the Sentry client (idempotent). Called from the
 * deferred logSetup schedule, and directly from logError so that an error
 * occurring before the idle deadline still forces init and gets captured.
 *
 * @returns {Promise|null} Promise resolving with the initialized Sentry module
 */
const initSentry = () => {
  if (!config.sentryDsn) return null;
  if (!sentryInitPromise) {
    sentryInitPromise = loadSentry().then((Sentry) => {
      Sentry.init({
        dsn: config.sentryDsn,
        environment: config.env,
        integrations: [],
        release: currentVersion(),
        ignoreErrors: IGNORE_ERRORS,
        denyUrls: [
          // Google ReCAPTCHA scripts
          /recaptcha\/releases\/.*/i,
          // react-big-calendar internals (e.g. selectDates getDate on empty slots)
          /react-big-calendar/,
          // Google's own gtag.js — "Cannot read properties of undefined
          // (reading 'update')" and "Promise.allSettled is not a function"
          // both originate inside gtag/js itself, not our code
          // (VANSITE-FRONTEND-4PT / -4Q6).
          /gtag\/js/,
          /googletagmanager\.com/,
        ],
        beforeSend(event, hint) {
          // Translation proxies (Google Translate serves us from
          // *.translate.goog) rewrite the document and break same-origin
          // requests — e.g. POST /api/events/tracking answers 405, once per
          // tracked interaction (VANSITE-FRONTEND-4QB). Errors from such a
          // session describe the proxy, not our app.
          if (/(^|\.)translate\.goog$/i.test(window.location.hostname)) {
            return null;
          }
          // Headless crawlers (HeadlessChrome 68 from AWS us-west-2 scraping
          // profile pages, VANSITE-FRONTEND-4Q5/4Q6) run below our browserslist
          // floor and trip on missing polyfills — not user-facing.
          if (/HeadlessChrome/i.test(window.navigator?.userAgent || '')) {
            return null;
          }
          // Drop errors that originate from inline HTML/JSON-LD with no
          // first-party JS stack — these are scrapers, bots, and browser
          // extensions polluting Sentry, not user-facing bugs.
          const exception = hint?.originalException;
          const message = exception?.message || event?.message || '';
          const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
          const hasFirstPartyFrame = frames.some(
            (f) => f.in_app === true || /\/static\/js\//.test(f.filename || ''),
          );
          if (!hasFirstPartyFrame && /@context["'\]\s]*\.toLowerCase/.test(message)) {
            return null;
          }
          return event;
        },
      });

      if (pendingUser !== undefined) {
        Sentry.setUser(pendingUser);
      }

      // Capture crashes that happened before init, then hand off to Sentry's
      // own global handlers.
      detachEarlyErrorListeners();
      earlyErrors.forEach((error) => Sentry.captureException(error));
      earlyErrors = [];

      return Sentry;
    });
  }
  return sentryInitPromise;
};

const currentVersion = () => {
  if (window.currentAppVersion) {
    return `app@${window.currentAppVersion}`;
  } else if (process.env.REACT_APP_VERSION) {
    window.currentAppVersion = process.env.REACT_APP_VERSION;
    return `web@${process.env.REACT_APP_VERSION}`;
  }
  return 'unknown';
};


const IGNORE_ERRORS = [
            // === Browser/network connectivity (transient, environmental) ===
            'Load failed',
            'Failed to fetch',
            'NetworkError',
            'Network request failed',
            /network error/i,
            'ERR_INTERNET_DISCONNECTED',
            'ERR_NETWORK',
            'The Internet connection appears to be offline',
            'A server with the specified hostname could not be found',
            'The network connection was lost',
            'Software caused connection abort',
            'No network connection available',
            'Connection reset',
            'connection was lost',
            // German Safari translations of network errors
            'Die Netzwerkverbindung wurde unterbrochen',
            'Es wurde kein Server mit dem angegebenen Hostnamen gefunden',
            // Android okhttp connection drops (cordova-plugin-advanced-http)
            'unexpected end of stream',
            /com\.android\.okhttp/,
            'SSLException',
            'SocketTimeoutException',
            'SocketException',
            // Android/Cordova DNS errors
            'Host could not be resolved',
            'UnknownHostException',
            'No address associated with hostname',
            'Unable to resolve host',
            // Chromium net errors
            'ERR_NAME_NOT_RESOLVED',
            'ERR_CONNECTION_RESET',
            'ERR_CONNECTION_CLOSED',
            'ERR_CONNECTION_TIMED_OUT',
            'ERR_TIMED_OUT',
            'ERR_ADDRESS_UNREACHABLE',
            // SDK / fetch timeouts
            'timeout exceeded',
            'Request timeout',
            'The request timed out',
            // === User-initiated cancellations (not bugs) ===
            // User navigated away / clicked cancel / killed share sheet
            'AbortError',
            /Fetch is aborted/i,
            /Abort due to cancellation of share/,
            // iOS/Safari reports a request cancelled by navigating away as
            // exactly "Abgebrochen" (German locale) / "cancelled", wrapped by
            // logError as "[fetch-datasource-entries-failed] Abgebrochen"
            // (VANSITE-FRONTEND-4SH). Anchored to the whole message so
            // "Zahlung abgebrochen" and the like still get through.
            /^(\[[\w-]+\] )?(Abgebrochen|cancelled)$/,
            /User denied Geolocation/,
            // === Backend rate-limit (expected) ===
            /Too many requests from this IP/,
            // === Origin returned HTML instead of JSON (server-side, not client bug) ===
            /^<!DOCTYPE/i,
            /Unexpected token '<'/,
            // === Bot / scraper / browser-extension noise ===
            // Inline JSON-LD scrapers and Twitter/Reddit cards reading non-existent fields.
            // Permissive on the chars between `@context` and `.toLowerCase` so it matches
            // both `@context.toLowerCase` and bracket variants like `["@context"].toLowerCase`.
            /@context["'\]\s]*\.toLowerCase/,
            // Generic "[object Object]" stringified by an extension treating it as JSON.
            // Quotes around the literal aren't always present, so don't require them.
            /\[object Object\][^\n]*is not valid JSON/,
            // Stack overflow nearly always originates from a browser extension or scraper
            'Maximum call stack size exceeded',
            // Google ReCAPTCHA internal errors (third-party, not actionable)
            /Cannot read properties of undefined \(reading 'hpm'\)/,
            // Facebook in-app browser injects scripts that call iOS native bridge
            /window\.webkit\.messageHandlers/,
            // Same injected script, other global: window._META.IABInnerFrame is
            // undefined outside Facebook's frame (VANSITE-FRONTEND-4SB).
            /_META\.IABInnerFrame/,
            // === Stripe / payment integration races (expected) ===
            /You cannot update Payment Request options while the payment sheet is showing/,
            // Customer revisits /checkout after the PaymentIntent already
            // succeeded; Stripe.js then refuses to update payment_method_types
            // on a terminal PI (VANSITE-FRONTEND-4HQ).
            /payment_method_types could not be updated/,
            /payment_intent_unexpected_state/,
            // === Auth race conditions (expected when user double-taps) ===
            'Login or logout already in progress',
            // === User-input validation errors that throw (libphonenumber etc.) ===
            'INVALID_COUNTRY',
            // === Browser-native page translation vs. React's DOM bookkeeping ===
            // Chrome/Safari replace text nodes in place while translating, so
            // React later removes or re-inserts a node the browser already
            // swapped out. Not fixable from our side short of suppressing
            // translation (VANSITE-FRONTEND-4MY).
            /Failed to execute 'removeChild' on 'Node'/,
            /Failed to execute 'insertBefore' on 'Node'/,
            // Safari's wording for the same native-page-translation-vs-React
            // DOM race, culprit removeChild([native code]) (VANSITE-FRONTEND-4N7).
            /The object can not be found here/,
            // === Cordova plugin lifecycle (plugin not always loaded) ===
            // Any plugin can be absent on a given build/platform — cordova's own
            // module loader throws this before our code can feature-detect.
            // Covers cordova-plugin-inappbrowser and ionic-plugin-deeplinks
            // (VANSITE-FRONTEND-47Z).
            /Module .* does not exist/,
            // Same cordova module-loader class, different plugin's wording
            // (cordova-android-movetasktoback.tsd, VANSITE-FRONTEND-4JN).
            /module .* not found/i,
            // WebView teardown: the native side is gone while JS still holds a
            // bridge reference (backgrounded/killed app). Nothing to fix on the
            // JS side (VANSITE-FRONTEND-4J9 / -4PJ).
            /Error invoking postMessage/,
            // Same WebView-teardown class via a different native bridge call
            // (VANSITE-FRONTEND-4RT / -4JB).
            /Error invoking exec: Java object is gone/,
];

/**
 * Set up error handling. If a Sentry DSN is provided a Sentry client will be
 * installed — but only once the browser is idle after `load`: downloading,
 * parsing and initializing Sentry must not compete with hydration for the
 * main thread during the load window that Core Web Vitals measure. Uncaught
 * errors before init are buffered and captured right after it; logError()
 * forces an immediate init.
 */
export const logSetup = () => {
  if (!config.sentryDsn || typeof window === 'undefined') return;
  attachEarlyErrorListeners();
  runWhenIdleAfterLoad(() => {
    const init = initSentry();
    if (init) {
      init.catch((e) => {
        console.error('Failed to load Sentry:', e);
      });
    }
  });
};

/**
 * Set user ID for the logger so that it
 * can be attached to Sentry issues.
 *
 * @param {string} userId ID of current user
 */
export const logSetUserId = (userId) => {
  if (config.sentryDsn) {
    // Don't force the (deferred) Sentry init just to attach the user id:
    // remember it and let initSentry apply it, or set it right away when the
    // client is already up.
    pendingUser = { id: userId };
    if (sentryInitPromise) {
      sentryInitPromise
        .then((Sentry) => {
          Sentry.setUser(pendingUser);
        })
        .catch((e) => {
          console.error('Failed to set user ID in Sentry:', e);
        });
    }
  }
};

/**
 * Clears the user ID.
 */
export const logClearUserId = () => {
  if (config.sentryDsn) {
    pendingUser = null;
    if (sentryInitPromise) {
      sentryInitPromise
        .then((Sentry) => {
          Sentry.setUser(null);
        })
        .catch((e) => {
          console.error('Failed to clear user ID in Sentry:', e);
        });
    }
  }
};

const printAPIErrorsAsConsoleTable = (apiErrors) => {
  /* Logging needs some console output */
  if (apiErrors != null && apiErrors.length > 0 && typeof console.table === 'function') {
    console.log('Errors returned by Marketplace API call:');
    console.table(apiErrors.map((err) => ({ status: err.status, code: err.code, ...err.meta })));
  }
};

// Non-actionable error signatures that should not be reported to Sentry.
// User-input validation, rate-limit hits, and conflict responses are expected
// runtime outcomes, not bugs worth tracking.
const NON_ACTIONABLE_API_CODES = new Set([
  'validation-invalid-params',
  'validation-invalid-value',
  'email-not-found',
  'email-taken',
  'expired-verification-code',
  'invalid-verification-code',
]);

const isSharetribeRequest = (e) =>
  e?.name === 'SharetribeApiError' ||
  /sharetribe/i.test(e?.config?.baseURL || '') ||
  /sharetribe/i.test(e?.config?.url || '');

// Rate-limited (429) and conflict (409) are user/session conditions, not bugs.
// Gateway timeouts and unavailability (502/503/504) are transient infra
// conditions — the upstream was slow or briefly down. The UI already degrades to
// a retry prompt; surfacing these (previously as a misleading SyntaxError) only
// flooded Sentry (VANSITE-FRONTEND-49X).
const TRANSIENT_STATUSES = new Set([409, 429, 502, 503, 504]);

// Only non-actionable when they come from the Sharetribe SDK: 401 is an
// expired/refresh-failed session, 403 a long-lived mobile WebView session gone
// stale or a banned user (VANSITE-FRONTEND-4A9/4AX/4B0), and 404 a deleted
// listing/user/transaction behind a stale link, which the UI already routes to a
// not-found page (VANSITE-FRONTEND-4BE). The same statuses from our own backend
// or a third party may be real auth or routing bugs and still get reported.
const STALE_SHARETRIBE_SESSION_STATUSES = new Set([401, 403, 404]);

// Statuses that describe a session or infrastructure condition rather than a
// defect in our code.
const isNonActionableStatus = (e, status) => {
  if (TRANSIENT_STATUSES.has(status)) return true;
  // Cordova HTTP plugin uses negative status codes for transport-layer
  // failures (DNS, SSL, timeout, abort) — environmental, not bugs.
  if (typeof status === 'number' && status < 0) return true;
  return STALE_SHARETRIBE_SESSION_STATUSES.has(status) && isSharetribeRequest(e);
};

// Payload shapes that carry no diagnosable signal at all: a bare DOM Event
// (serialises to `{ isTrusted: true }`) is what a failed resource/XHR load hands
// its error handler — no message, no status, no stack, so every call site
// collapses into one undiagnosable issue (VANSITE-FRONTEND-4MQ). A
// GeolocationPositionError is the user declining or the fix timing out;
// IGNORE_ERRORS already lists /User denied Geolocation/, but that never matches
// because logError wraps the non-Error payload in a synthetic Error whose
// message does not carry the text (VANSITE-FRONTEND-491). Both constructors are
// probed defensively — neither exists under SSR or in Jest.
const isEnvironmentalPayload = (e) => {
  if (typeof Event !== 'undefined' && e instanceof Event) return true;
  return typeof GeolocationPositionError !== 'undefined' && e instanceof GeolocationPositionError;
};

// Voucherify 404 for a voucher code the user typed themselves, surfaced through
// the generic "Third Party request failed" wrapper with the Voucherify error
// body nested under `data`. Scoped to the `not_found` key so an unrelated
// Voucherify 404 (e.g. a real outage) still surfaces
// (VANSITE-FRONTEND-4MB / -4PQ).
const isVoucherCodeMiss = (e, status) =>
  (status === 404 || e?.data?.status === 404) && e?.data?.key === 'not_found';

export const isNonActionableError = (e, apiErrors) => {
  const status = e?.status;
  if (isNonActionableStatus(e, status)) return true;
  // Any apiError carrying a non-actionable user-input code, whatever the
  // overall status — which covers the 400 the SDK and our API answer with.
  if (apiErrors?.some((err) => NON_ACTIONABLE_API_CODES.has(err?.code))) return true;
  if (isEnvironmentalPayload(e)) return true;
  if (isVoucherCodeMiss(e, status)) return true;
  // Origin returned an HTML page instead of JSON (CDN error page, not a client bug)
  const message = typeof e?.message === 'string' ? e.message : '';
  return message.startsWith('<!DOCTYPE') || message.startsWith('<html');
};

const isCodeValue = (value) =>
  (typeof value === 'string' && value !== '') || typeof value === 'number';

const parseBody = (body) => {
  if (typeof body !== 'string') return body;
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
};

/**
 * Error code of a payload whose server body is not in Sharetribe's error
 * format (`responseApiErrorInfo` finds nothing in it). The Cordova HTTP plugin
 * hands the raw response body over as a JSON string in `error`; our own API
 * answers with `errorCode` or `{ error: { code } }`.
 *
 * @param {object} e Error-like payload
 * @returns {string|number|undefined} The code, or undefined if the body has none
 */
export const payloadErrorCode = (e) => {
  const body = parseBody(e?.error);
  const code = body?.errorCode ?? body?.code ?? body?.error?.code;
  return isCodeValue(code) ? code : undefined;
};

/**
 * Grouping fingerprint for non-Error payloads that logError wraps in a
 * synthetic Error. Their stack always points at the wrapper in this file, so
 * Sentry's stack-based grouping folded unrelated failures into a single issue
 * (VANSITE-FRONTEND-47M) — group them by their logical error code and HTTP
 * status instead.
 *
 * @param {string} code Error code passed to logError
 * @param {number} [status] HTTP status of the wrapped payload, if any
 * @param {string|number} [apiErrorCode] Sharetribe API error code, or the code
 *   from the payload body (see `payloadErrorCode`) when there is none
 * @returns {string[]} Sentry fingerprint
 */
export const syntheticErrorFingerprint = (code, status, apiErrorCode) => {
  const fingerprint = [code];
  if (status != null) {
    fingerprint.push(String(status));
  }
  // code + status alone is too coarse for the API wrappers: every failing
  // `post-cordova-error` 400 collapsed into a single Sentry issue regardless of
  // cause, mixing `validation-invalid-value` (negative payin on
  // /api/initiate-privileged), `validation-invalid-params` and plain
  // `bad-request` under one group with one misleading user count
  // (VANSITE-FRONTEND-4MG). The Sharetribe error code separates them.
  if (apiErrorCode) {
    fingerprint.push(String(apiErrorCode));
  }
  return fingerprint;
};

// console.error for logError, console.warn for logWarning — looked up at call
// time (not captured at module load) so tests that jest.spyOn(console, ...)
// after this module loads still see the calls.
const consoleMethod = (level) => (level === 'warning' ? console.warn : console.error);

const safeSerialize = (e) => {
  let serialized;
  try {
    serialized = JSON.stringify(e, null, 2);
  } catch {
    // e may contain cyclic references — fall back to a plain string.
    serialized = String(e);
  }
  return serialized;
};

const logToConsole = (level, e, code, data) => {
  if (e?.status === 404) return;
  const print = consoleMethod(level);
  if (e instanceof Error) {
    print(code, e.message, e.stack, data);
  } else {
    print(code, safeSerialize(e), data);
  }
};

/**
 * Sends `e` to Sentry at the given severity level, wrapping non-Error
 * payloads in a synthetic Error first (see `syntheticErrorFingerprint`).
 * Shared by logError and logWarning.
 *
 * @param {'error'|'warning'} level Sentry scope level
 * @param {Error|object} e Error or error-like payload
 * @param {string} code Error code
 * @param {object} data Additional data to be sent to Sentry
 * @param {object[]} apiErrors Parsed API errors from responseApiErrorInfo
 */
const sendToSentry = (level, e, code, data, apiErrors) => {
  if (!config.sentryDsn) return;
  // Force init (idempotent): an error before the deferred idle init must
  // still reach Sentry.
  initSentry()
    .then((Sentry) => {
      const extra = { ...data, apiErrorData: apiErrors };

      // If the error is not a proper Error instance (e.g. SDK response object),
      // wrap it so Sentry captures a stack trace and groups it properly.
      let error = e;
      let fingerprint = null;
      if (!(e instanceof Error)) {
        const message = e?.message || e?.detail || e?.error || e?.statusText || code;
        const status = e?.status;
        error = new Error(`[${code}] ${status ? `${status} - ` : ''}${message}`);
        fingerprint = syntheticErrorFingerprint(
          code,
          status,
          apiErrors[0]?.code ?? payloadErrorCode(e)
        );
        try {
          extra.originalError = JSON.parse(JSON.stringify(e || {}));
        } catch {
          extra.originalError = String(e);
        }
      }

      Sentry.withScope((scope) => {
        scope.setTag('code', code);
        scope.setLevel(level);
        if (fingerprint) {
          scope.setFingerprint(fingerprint);
        }
        Object.keys(extra).forEach((key) => {
          scope.setExtra(key, extra[key]);
        });
        Sentry.captureException(error);
      });
    })
    .catch((err) => {
      consoleMethod(level)(`Failed to log ${level} to Sentry:`, err);
      // Still print API errors even if Sentry fails
      printAPIErrorsAsConsoleTable(apiErrors);
    });
};

/**
 * Logs an exception. If Sentry is configured
 * sends the error information there. Otherwise
 * prints the error to the console.
 *
 * @param {Error} e Error that occurred
 * @param {string} code Error code
 * @param {object} data Additional data to be sent to Sentry
 */
export const logError = (e, code, data = {}) => {
  const apiErrors = responseApiErrorInfo(e);
  logToConsole('error', e, code, data);
  if (isNonActionableError(e, apiErrors)) {
    return;
  }
  sendToSentry('error', e, code, data, apiErrors);
};

/**
 * Logs a handled, non-critical condition. Same mechanics as `logError`
 * (console output, synthetic-Error wrapping for non-Error payloads,
 * fingerprint, the `isNonActionableError` gate) but reports to Sentry at
 * `warning` level via console.warn instead of console.error — for cases the
 * app already recovered from, where the event is worth tracking but not
 * paging on.
 *
 * @param {Error} e Error that occurred
 * @param {string} code Error code
 * @param {object} data Additional data to be sent to Sentry
 */
export const logWarning = (e, code, data = {}) => {
  const apiErrors = responseApiErrorInfo(e);
  logToConsole('warning', e, code, data);
  if (isNonActionableError(e, apiErrors)) {
    return;
  }
  sendToSentry('warning', e, code, data, apiErrors);
};
